// (c) 2026 Owl Consultations. All rights reserved.
// Proprietary and confidential — licensed use only. Unauthorized use,
// copying, or distribution is prohibited and may be prosecuted.

// D-CRX-80 PR1 — THE analyte vocabulary. One list, three former copies.
//
// WHY THIS FILE EXISTS (the defect it closes): the token is the stored identity that
// carerx_labs / carerx_screening rows and the enrollment door match on. Three surfaces each
// carried their own copy of the vocabulary and they did not agree:
//
//   · DrugReferencePage  ANALYTES  [token, label]  -> stored 'tb_igra'          (correct)
//   · DrugRecordEditV4   ANALYTES  [label]         -> stored 'tb screening (igra)'  (WRONG)
//   · enrollmentFormSpec screening.rows            -> matched 'tb_igra' only, 3 rows
//
// DrugRecordEditV4 is the LIVE editor (DrugReferencePage's LEGACY_EDITOR === false), so the
// tokens pharmacies actually write today are lower-cased DISPLAY LABELS. Nothing matches them:
// not the lab tracker, not the enrollment door's lab check, not the enrollment form's screening
// table. The user-visible symptom is "the labs are missing" — for every drug configured since
// V4 went live.
//
// So this module is BOTH halves of the fix:
//   1. ANALYTES — the single vocabulary every surface renders from, and
//   2. normalizeAnalyte() — a READ-SIDE repair that maps an already-stored label-shaped value
//      back to its token. Rows already written to a tenant DB are interpreted, never rewritten
//      (no tenant-data writes). A backfill, if it ever happens, is a separate approved change.
//
// Adding an analyte: add it here and nowhere else. A token is permanent once stored — change a
// LABEL freely, never a token.

export interface Analyte {
  /** Stored identity. Permanent. What carerx_labs.analyte and required_baselabs hold. */
  token: string
  /** Display name on forms and pickers. Safe to reword. */
  label: string
  /** Short name for grid pills and narrow columns. */
  pill: string
}

export const ANALYTES: Analyte[] = [
  { token: 'tb_igra', label: 'TB screening (IGRA)', pill: 'TB' },
  { token: 'hep_b', label: 'Hepatitis B panel', pill: 'Hep B' },
  { token: 'hep_c', label: 'Hepatitis C antibody', pill: 'Hep C' },
  { token: 'hiv', label: 'HIV screen', pill: 'HIV' },
  { token: 'jcv', label: 'JCV antibody', pill: 'JCV' },
  { token: 'cbc', label: 'CBC with differential', pill: 'CBC' },
  { token: 'cmp', label: 'CMP', pill: 'CMP' },
  { token: 'lft', label: 'Hepatic panel (LFTs)', pill: 'LFTs' },
  { token: 'lipid', label: 'Lipid panel', pill: 'Lipid' },
  { token: 'uric_acid', label: 'Uric acid', pill: 'Uric' },
  { token: 'g6pd', label: 'G6PD', pill: 'G6PD' },
  { token: 'quant_ig', label: 'Quantitative immunoglobulins (IgG/IgA/IgM)', pill: 'Ig' },
  { token: 'crp', label: 'CRP', pill: 'CRP' },
  { token: 'esr', label: 'ESR', pill: 'ESR' },
  { token: 'ck', label: 'CK (creatine kinase)', pill: 'CK' },
  { token: 'vitamin_d', label: 'Vitamin D, 25-hydroxy', pill: 'Vit D' },
]

const BY_TOKEN = new Map(ANALYTES.map(a => [a.token, a]))

/**
 * Every spelling that has ever been written for a token, lower-cased.
 *
 * The label forms are what DrugRecordEditV4 stored (`label.toLowerCase()`), including the label
 * text it used that differs from this module's — 'quantitative immunoglobulins' had no
 * parenthetical in V4's list. Legacy seed spellings (127/258) ride along in the same table
 * because they are the same class of problem: a stored string that means a known analyte.
 */
const ALIASES: Record<string, string> = {}
for (const a of ANALYTES) {
  ALIASES[a.token] = a.token
  ALIASES[a.label.toLowerCase()] = a.token
  ALIASES[a.pill.toLowerCase()] = a.token
}
// V4's own label list, verbatim, lower-cased — the exact strings sitting in required_baselabs now.
Object.assign(ALIASES, {
  'tb screening (igra)': 'tb_igra',
  'hepatitis b panel': 'hep_b',
  'hepatitis c antibody': 'hep_c',
  'hiv screen': 'hiv',
  'jcv antibody': 'jcv',
  'cbc with differential': 'cbc',
  'hepatic panel (lfts)': 'lft',
  'lipid panel': 'lipid',
  'uric acid': 'uric_acid',
  'quantitative immunoglobulins': 'quant_ig',
  // Seed/legacy spellings observed in the formulary seeds (127, 258) and in the owner's
  // 2026-09-06 repro, where the enrollment door rendered "TB SCREENING" — so the stored token is
  // 'tb_screening'/'tb screening', NOT the editor's 'tb screening (igra)'. More than one writer
  // has produced more than one spelling; the read side tolerates all of them. An analyte NOT
  // listed here is still never dropped — normalizeAnalyte passes it through, so a row renders
  // for it and matches lab rows spelt the same way.
  'tb': 'tb_igra',
  'tb_test': 'tb_igra',
  'tb screening': 'tb_igra',
  'tb_screening': 'tb_igra',
  'igra': 'tb_igra',
  'cbc with diff': 'cbc',
  'cbc w/ diff': 'cbc',
  'jcv_ab': 'jcv',
  'hepb': 'hep_b',
  'hep b': 'hep_b',
  'hepatitis b': 'hep_b',
  'hepc': 'hep_c',
  'hepatitis c': 'hep_c',
  'lfts': 'lft',
  'cbc_diff': 'cbc',
  // Observed in the LIVE seed 2026-09-06 (owlrx-local carerx_formulary): stored values are
  // ["TB screening","CBC"], ["Hepatic panel","Lipid panel"] — bare labels, several NOT matching
  // the editor's own full label ("Hepatic panel" has no "(LFTs)"). Read the data, do not infer
  // the spellings from the writer (CLAUDE.md §7).
  'hepatic panel': 'lft',
  'liver panel': 'lft',
  'immunoglobulins': 'quant_ig',
  'quant ig': 'quant_ig',
  // CareRx wave 1 #20 — CK and 25-hydroxy vitamin D. Mirrors AnalyteVocabulary.cs.
  'cpk': 'ck',
  'creatine kinase': 'ck',
  'creatine phosphokinase': 'ck',
  'vitamin d': 'vitamin_d',
  'vit d': 'vitamin_d',
  '25-oh vitamin d': 'vitamin_d',
  '25(oh)d': 'vitamin_d',
  '25-hydroxyvitamin d': 'vitamin_d',
})

/**
 * Map a stored analyte string to its canonical token.
 *
 * Unknown values pass through lower-cased and trimmed rather than being dropped: a pharmacy may
 * have configured an analyte this vocabulary does not name, and losing it is the very failure
 * this module exists to end. The caller renders an unrecognised token as-is (upper-cased), the
 * way the lab tracker already does.
 */
export function normalizeAnalyte(raw: string | null | undefined): string {
  const k = String(raw ?? '').trim().toLowerCase()
  if (!k) return ''
  return ALIASES[k] ?? k.replace(/\s+/g, '_')
}

/** Normalize a stored list, de-duplicating tokens that collapse together (label + token forms). */
export function normalizeAnalytes(raw: readonly (string | null | undefined)[] | null | undefined): string[] {
  const out: string[] = []
  for (const v of raw ?? []) {
    const t = normalizeAnalyte(v)
    if (t && !out.includes(t)) out.push(t)
  }
  return out
}

/** Display label for a token. An analyte outside the vocabulary renders as its own token. */
export function analyteLabel(token: string): string {
  return BY_TOKEN.get(normalizeAnalyte(token))?.label ?? String(token ?? '').replace(/_/g, ' ').toUpperCase()
}

/** Short pill label for grids. Same fallback rule as analyteLabel. */
export function analytePill(token: string): string {
  return BY_TOKEN.get(normalizeAnalyte(token))?.pill ?? String(token ?? '').replace(/_/g, ' ').toUpperCase()
}

/** True when the token is one this vocabulary names (used to flag legacy entries in pickers). */
export function isKnownAnalyte(token: string): boolean {
  return BY_TOKEN.has(normalizeAnalyte(token))
}
